"use client"

import { whenIdle } from "@/lib/when-idle"

// The pool of network ad ZONES the browse grid cycles through. A pool entry is a zone descriptor (the
// script that asks the network for a creative), not a creative: every render still asks the network for
// a fresh ad. That is why it is safe to share and to cache.
//
// Module-level on purpose. This used to live in BooruGrid's effect, and every BooruGrid mount fetched a
// whole new pool of 6 and replaced the published one (measured ~20 fetches per page load, then 12 after a
// per-instance guard: the grid mounts twice). Each replacement swapped the creative, and its size, in ad
// slots already on screen. Now one pool is fetched per ad tier no matter how many grids mount, and a
// recent pool is reused (also across page loads) so slots can be given their real size on the first paint.

export interface NetworkAdPoolEntry {
  displayJs: string
  zoneId: string | null
  zoneWidth: number | null
  zoneHeight: number | null
}

export interface NetworkAdPool {
  entries: NetworkAdPoolEntry[]
  videoUrl: string | null
}

const POOL_SIZE = 6
const TTL_MS = 20 * 60 * 1000
const STORAGE_KEY = "goonru_net_ad_pool_v1"

const memory = new Map<string, { at: number; pool: NetworkAdPool }>()
const inflight = new Map<string, Promise<NetworkAdPool>>()

function readStorage(): Record<string, { at: number; pool: NetworkAdPool }> {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}")
  } catch {
    return {}
  }
}

/** A recent pool for this tier if we already have one, else null. Synchronous; safe to call during render. */
export function peekNetworkAdPool(tier: number | string): NetworkAdPool | null {
  const key = String(tier)
  const now = Date.now()
  const hit = memory.get(key)
  if (hit && now - hit.at < TTL_MS) return hit.pool
  if (typeof window === "undefined") return null
  const stored = readStorage()[key]
  if (stored && now - stored.at < TTL_MS && stored.pool?.entries?.length) {
    memory.set(key, stored)
    return stored.pool
  }
  return null
}

function remember(key: string, pool: NetworkAdPool) {
  const entry = { at: Date.now(), pool }
  memory.set(key, entry)
  try {
    const all = readStorage()
    all[key] = entry
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
  } catch {
    // quota / private mode: the in-memory copy still works
  }
}

/**
 * Resolves with the pool for this tier: the cached one if recent, otherwise one shared in-flight request
 * round (2 creatives immediately, 4 more after a short delay so they don't land on top of the first
 * search). Never rejects; an empty `entries` means no ads came back.
 */
export function loadNetworkAdPool(tier: number | string, tagsParam = ""): Promise<NetworkAdPool> {
  const key = String(tier)
  const cached = peekNetworkAdPool(key)
  if (cached) return Promise.resolve(cached)
  const running = inflight.get(key)
  if (running) return running

  const fetchOne = () =>
    fetch(`/api/ads/network-grid-ad?tier=${key}${tagsParam}`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)

  const wave1 = Promise.all([fetchOne(), fetchOne()])
  // 1.2s grace, then an idle moment (never later than +1.5s more).
  const wave2 = new Promise<any[]>((resolve) => {
    setTimeout(
      () => whenIdle(() => Promise.all(Array.from({ length: POOL_SIZE - 2 }, fetchOne)).then(resolve), 1500),
      1200,
    )
  })

  const run = Promise.all([wave1, wave2]).then(([r1, r2]) => {
    const seen = new Set<string>()
    const entries: NetworkAdPoolEntry[] = []
    let videoUrl: string | null = null
    for (const d of [...r1, ...r2]) {
      if (!d?.display_js) continue
      const zoneKey = d.zone_id ?? d.display_js
      if (seen.has(zoneKey)) continue
      seen.add(zoneKey)
      entries.push({
        displayJs: d.display_js,
        zoneId: d.zone_id ?? null,
        zoneWidth: typeof d.creative_width === "number" ? d.creative_width : null,
        zoneHeight: typeof d.creative_height === "number" ? d.creative_height : null,
      })
      if (d.video_url) videoUrl = d.video_url
    }
    const pool = { entries, videoUrl }
    if (entries.length > 0) remember(key, pool)
    inflight.delete(key)
    return pool
  })
  inflight.set(key, run)
  return run
}
